import { useState, useContext, useEffect } from "react";
import { Modal, Box, Button, TextField, Icon } from "@mui/material";
import { useAddNote } from "../api/notes";
import {
  DrawingContext,
  DrawingMode,
  DrawingFeatureType,
} from "../contexts/DrawingContext";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";

interface AddNoteModalProps {
  onClose?: () => void;
}

const AddNoteModal = ({ onClose }: AddNoteModalProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { drawingMode, setDrawingMode, setDrawingFeatureType } =
    useContext(DrawingContext);
  const { mutateAsync: addNote } = useAddNote(projectId!);

  const [open, setOpen] = useState(false);
  const [fullText, setFullText] = useState<string>("");
  const [date, setDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [photo, setPhoto] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ text?: string }>({});

  // open modal when user presses Enter after drawing point
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && drawingMode === DrawingMode.NOTE) {
      setOpen(true);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [drawingMode]);

  const reset = () => {
    setFullText("");
    setDate(dayjs().format("YYYY-MM-DD"));
    setPhoto(null);
    setDrawingMode(DrawingMode.NONE);
    setDrawingFeatureType(DrawingFeatureType.NONE);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
    if (onClose) onClose();
  };

  const validate = () => {
    const errs: { text?: string } = {};
    if (!fullText) errs.text = "Note text required";
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      await addNote({ fullText, dateAdded: date, photo });
      handleClose();
    } catch (e) {
      console.error("Add note failed", e);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Box
        sx={{
          padding: 2,
          borderRadius: 8,
          backgroundColor: "white",
          width: "25%",
          minWidth: 300,
        }}
      >
        <h2>Add Note</h2>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline" }}>
          <Icon sx={{ m: 1, color: "#00d4ad" }}>event</Icon>
          <TextField
            label="Date"
            type="date"
            value={date}
            fullWidth
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline" }}>
          <Icon sx={{ m: 1, color: "#00d4ad" }}>note</Icon>
          <TextField
            label="Note"
            multiline
            minRows={3}
            value={fullText}
            fullWidth
            onChange={(e) => setFullText(e.target.value)}
            error={!!errors.text}
            helperText={errors.text}
            sx={{ mb: 2 }}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
        </div>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={handleSubmit} variant="contained" sx={{ backgroundColor: "#00d4ad" }}>
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddNoteModal;
